import type { IntegrationsTranslator } from '@/lib/integrations/integrations-translator'

/**
 * Why a Stripe install came back to the integration page (`?stripe_error=`),
 * and what the page says about it. Client-safe: the callback sends these
 * codes and the page shows the copy (`integrations.stripe.connectErrors`), so
 * a new code can't ship without words.
 */
export const STRIPE_CONNECT_ERROR_KEY = {
  provider_denied: 'providerDenied',
  missing_code: 'missingCode',
  // The common cause: a test-mode or sandbox install, whose code the live
  // platform key can't exchange (Brew offers the live install link only).
  token_exchange_failed: 'tokenExchangeFailed',
  provisioning_failed: 'connectFailed',
  connection_persist_failed: 'connectFailed',
  missing_state: 'restart',
  invalid_state: 'restart',
  expired_state: 'restart',
  context_mismatch: 'restart',
} as const satisfies Record<string, string>

export type StripeConnectErrorCode = keyof typeof STRIPE_CONNECT_ERROR_KEY

function isStripeConnectErrorCode(
  code: string
): code is StripeConnectErrorCode {
  // Own keys only: `?stripe_error=__proto__` must not read Object.prototype.
  return Object.hasOwn(STRIPE_CONNECT_ERROR_KEY, code)
}

/** The page's words for a `stripe_error`, or null when there is none. */
export function describeStripeConnectError(
  t: IntegrationsTranslator,
  code: string | null
): string | null {
  if (!code) {
    return null
  }
  // An unknown code reads as a plain failed connect.
  const key = isStripeConnectErrorCode(code)
    ? STRIPE_CONNECT_ERROR_KEY[code]
    : 'connectFailed'
  return t(`stripe.connectErrors.${key}`)
}
